
import React, { useState, useCallback, useEffect } from 'react';
import { useQuery, gql, useMutation } from '@apollo/client';
import { debounce } from 'lodash';
import { FaEdit, FaTrash, FaSave, FaTimes, FaPlus } from 'react-icons/fa';

// GraphQL queries and mutations
const GET_ALL_CATEGORIES = gql`
  query GetAllCategories {
    getAllCategory {
      category_id
      category_name
      category_description
      category_price
      addedat
    }
  }
`;

const ADD_CATEGORY = gql`
  mutation AddCategory($category_name: String!, $category_description: String!, $category_price: Int!,$category_media :String!) {
    createCategory(category_name: $category_name, category_description: $category_description, category_price: $category_price,category_media:$category_media) {
      category_id
      category_name
      category_description
      category_price
      category_media
      addedat
    }
  }
`;

const UPDATE_CATEGORY = gql`
  mutation UpdateCategory($category_id: Int!, $category_name: String, $category_description: String, $category_price: Int) {
    updateCategory(category_id: $category_id, category_name: $category_name, category_description: $category_description, category_price: $category_price) {
      category_id
      category_name
      category_description
      category_price
      addedat
    }
  }
`;

const DELETE_CATEGORY = gql`
  mutation DeleteCategory($category_id: Int!) {
    deleteCategory(category_id: $category_id)
  }
`;

const Categories = () => {
  const [categories, setCategories] = useState([]);
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [searchTerms, setSearchTerms] = useState({
    category_id: '',
    category_name: '',
    category_description: '',
    addedat: '',
    category_price: '',
  });
  const [editingCategoryId, setEditingCategoryId] = useState(null);
  const [editingValues, setEditingValues] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [categoriesPerPage] = useState(7);
  const [saveError, setSaveError] = useState(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [newCategory, setNewCategory] = useState({ category_name: '', category_description: '', category_price: '' ,category_media:''});

  const { loading, error, data } = useQuery(GET_ALL_CATEGORIES);
  const [addCategory] = useMutation(ADD_CATEGORY);
  const [updateCategory] = useMutation(UPDATE_CATEGORY);
  const [deleteCategory] = useMutation(DELETE_CATEGORY);

  useEffect(() => {
    if (data && data.getAllCategory) {
      setCategories(data.getAllCategory);
      setFilteredCategories(data.getAllCategory);
    }
  }, [data]);

  const debouncedSearch = useCallback(
    debounce((searchTerms) => {
      const filtered = categories.filter(category => {
        return Object.entries(searchTerms).every(([key, value]) => {
          if (!value) return true;
          return category[key]?.toString().toLowerCase().includes(value.toLowerCase());
        });
      });
      setFilteredCategories(filtered);
      setCurrentPage(1);
    }, 300),
    [categories]
  );

  const handleSearchChange = (e) => {
    const { name, value } = e.target;
    setSearchTerms(prev => ({ ...prev, [name]: value }));
    debouncedSearch({ ...searchTerms, [name]: value });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (['category_name', 'category_description', 'category_price'].includes(name)) {
      setEditingValues(prev => ({ ...prev, [name]: value }));
      setSaveError(null);
    }
  };

  const handleNewCategoryChange = (e) => {
    const { name, value } = e.target;
    setNewCategory(prev => ({ ...prev, [name]: value }));
  };

  const handleAddCategory = async () => {
    try {
      const result = await addCategory({
        variables: {
          category_name: newCategory.category_name,
          category_description: newCategory.category_description,
          category_price: parseInt(newCategory.category_price),
          category_media:newCategory.category_media
        }
      });
      const updatedCategories = [...categories, result.data.createCategory];
      setCategories(updatedCategories);
      setFilteredCategories(updatedCategories);
      setIsAddModalOpen(false);
      setNewCategory({ category_name: '', category_description: '', category_price: '' });
    } catch (error) {
      setSaveError("Error adding new category: " + error.message);
    }
  };

  const handleUpdateCategory = async (categoryId) => {
    try {
      const currentCategory = categories.find(cat => cat.category_id === categoryId);
      const updatedValues = {
        category_id: categoryId,
        category_name: editingValues.category_name || currentCategory.category_name,
        category_description: editingValues.category_description || currentCategory.category_description,
        category_price: parseInt(editingValues.category_price) || currentCategory.category_price
      };

      const result = await updateCategory({
        variables: updatedValues
      });

      const updatedCategories = categories.map(cat => 
        cat.category_id === categoryId ? result.data.updateCategory : cat
      );
      setCategories(updatedCategories);
      setFilteredCategories(updatedCategories);

      setEditingCategoryId(null);
      setEditingValues({});
    } catch (error) {
      setSaveError("Error updating category: " + error.message);
    }
  };

  const handleDeleteCategory = async (categoryId) => {
    if (window.confirm('Are you sure you want to delete this category?')) {
      try {
        await deleteCategory({
          variables: { category_id: categoryId }
        });
        const updatedCategories = categories.filter(cat => cat.category_id !== categoryId);
        setCategories(updatedCategories);
        setFilteredCategories(updatedCategories);
      } catch (error) {
        setSaveError("Error deleting category: " + error.message);
      }
    }
  };

  const headers = [
    { key: 'category_id', label: 'Category Id' },
    { key: 'category_name', label: 'Category Name' },
    { key: 'category_description', label: 'Description' },
    { key: 'addedat', label: 'Added At' },
    { key: 'category_price', label: 'Price' },
  ];

  const indexOfLastCategory = currentPage * categoriesPerPage;
  const indexOfFirstCategory = indexOfLastCategory - categoriesPerPage;
  const currentCategories = filteredCategories.slice(indexOfFirstCategory, indexOfLastCategory);

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  if (loading) return <div className="flex items-center justify-center h-screen"><div className="text-2xl font-semibold text-gray-600">Loading...</div></div>;
  if (error) return <div className="flex items-center justify-center h-screen"><div className="text-2xl font-semibold text-red-600">Error: {error.message}</div></div>;

  return (
    <div className="flex flex-col h-full p-6">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Corporate Categories List</h1>
        <button
          onClick={() => setIsAddModalOpen(true)}
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded inline-flex items-center"
        >
          <FaPlus className="mr-2" /> Add Category
        </button>
      </div>
      {saveError && (
        <div className="bg-red-100 border-l-4 border-red-500 text-red-700 p-4 mb-4" role="alert">
          <p>{saveError}</p>
        </div>
      )}
      <div className="flex-grow overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {headers.map(header => (
                  <th key={header.key} className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider whitespace-nowrap">
                    {header.label}
                  </th>
                ))}
                <th className="px-2 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              <tr className="bg-white">
                {headers.map(header => (
                  <td key={header.key} className="px-2 py-2">
                    <input
                      type="text"
                      name={header.key}
                      placeholder={`Search ${header.label}`}
                      value={searchTerms[header.key]}
                      onChange={handleSearchChange}
                      className="w-full p-1 border rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </td>
                ))}
                <td></td>
              </tr>
              {currentCategories.map((category) => (
                <tr key={category.category_id} className="hover:bg-gray-50">
                  {headers.map(header => (
                    <td key={header.key} className="px-2 py-2 whitespace-normal text-xs text-gray-800">
                      {editingCategoryId === category.category_id && ['category_name', 'category_description', 'category_price'].includes(header.key) ? (
                        <input
                          type={header.key === 'category_price' ? 'number' : 'text'}
                          name={header.key}
                          value={editingValues[header.key] !== undefined ? editingValues[header.key] : category[header.key]}
                          onChange={handleInputChange}
                          className="w-full p-1 border rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                      ) : (
                        category[header.key]
                      )}
                    </td>
                  ))}
                  <td className="px-2 py-2 whitespace-nowrap">
                    {editingCategoryId === category.category_id ? (
                      <>
                        <button
                          onClick={() => handleUpdateCategory(category.category_id)}
                          className="text-green-600 hover:text-green-800 mr-2"
                        >
                          <FaSave />
                        </button>
                        <button
                          onClick={() => setEditingCategoryId(null)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <FaTimes />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => { setEditingCategoryId(category.category_id); setEditingValues(category); }}
                          className="text-blue-600 hover:text-blue-800 mr-2"
                        >
                          <FaEdit />
                        </button>
                        <button
                          onClick={() => handleDeleteCategory(category.category_id)}
                          className="text-red-600 hover:text-red-800"
                        >
                          <FaTrash />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="flex justify-center mt-6">
        {Array.from({ length: Math.ceil(filteredCategories.length / categoriesPerPage) }, (_, i) => (
          <button
            key={i + 1}
            onClick={() => paginate(i + 1)}
            className={`mx-1 px-3 py-1 border rounded ${currentPage === i + 1 ? 'bg-blue-500 text-white' : 'bg-white text-blue-500'} hover:bg-blue-500 hover:text-white`}
          >
            {i + 1}
          </button>
        ))}
      </div>
      {isAddModalOpen && (
        <div className="fixed inset-0 flex items-center ml-40 justify-center">
          <div className="bg-white rounded shadow-lg  p-8">
            <h2 className="text-lg font-bold mb-4">Add New Category</h2>
            <input
              type="text"
              name="category_name"
              placeholder="Category Name"
              value={newCategory.category_name}
              onChange={handleNewCategoryChange}
              className="w-full p-2 border rounded mb-4"
            />
            <input
              type="text"
              name="category_description"
              placeholder="Category Description"
              value={newCategory.category_description}
              onChange={handleNewCategoryChange}
              className="w-full p-2 border rounded mb-4"
            />
            <input
              type="number"
              name="category_price"
              placeholder="Category Price"
              value={newCategory.category_price}
              onChange={handleNewCategoryChange}
              className="w-full p-2 border rounded mb-4"
            />
             <input
              type="text"
              name="category_media"
              placeholder="Category Media"
              value={newCategory.category_media}
              onChange={handleNewCategoryChange}
              className="w-full p-2 border rounded mb-4"
            />
            <div className="flex justify-end">
              <button
                onClick={handleAddCategory}
                className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-2 px-4 rounded mr-2"
              >
                Add
              </button>
              <button
                onClick={() => setIsAddModalOpen(false)}
                className="bg-gray-300 hover:bg-gray-400 text-gray-700 font-bold py-2 px-4 rounded"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Categories;


